import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { OFFLINE_DURABLE_GC_TIME, projectKeys } from "@/lib/query-keys";
import { isPinnedSync } from "@/offline/pins";
import type { ProjectDetail } from "@/types/api";

export function useProject(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  // No throwOnError. Errors stay in query.error and consumers decide whether
  // to render cached state, an inline error, or null. Previously this hook
  // threw to the root QueryErrorBoundary, which nuked the whole project shell
  // when a sibling records query failed offline.
  return useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: () =>
      fetchWithAuth<ProjectDetail>(`/projects/1.0/projects/${projectId}/`),
    // GC-immune only while pinned — mirrors what the persister saves for this
    // project. Non-pinned projects keep the default gcTime; they were never
    // saved offline, so retaining them in memory would only waste it.
    gcTime: isPinnedSync(projectId) ? OFFLINE_DURABLE_GC_TIME : undefined,
  });
}
